import { FC, useState } from "react";
import { FactureAPayer } from "../../lib/interfaces/factures.interface";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { loadStripe } from "@stripe/stripe-js";
import { toast } from "sonner";
import { useCreateStripeCheckoutSessionPourFacture } from "../../lib/hooks/useFacture";
import { CreditCard, Landmark, Mail, ReceiptText } from "lucide-react";

interface Props {
  facture: FactureAPayer;
}

const CommentPayerFactureDialog: FC<Props> = ({ facture }) => {
  const [loading, setLoading] = useState(false);
  const { mutateAsync: createStripeCheckoutSessionPourFacture } = useCreateStripeCheckoutSessionPourFacture();
  const payerParCarte = async () => {
    try {
      setLoading(true);
      const stripeCheckoutSessionId = await createStripeCheckoutSessionPourFacture({ factureId: facture.id });
      const stripe = await loadStripe(
        facture.sna ? `${import.meta.env.VITE_SNA_STRIPE_KEY}` : `${import.meta.env.VITE_STRIPE_KEY}`
      );
      stripe?.redirectToCheckout({ sessionId: stripeCheckoutSessionId });
    } catch (error) {
      toast.error("Une erreur s'est produite. Veuillez réessayer ultérieurement ou choisir un autre mode de paiement.");
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="w-full bg-[#173f35] hover:bg-[#23594a] sm:w-auto">Régler la facture</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto p-0 sm:max-w-[580px] sm:rounded-2xl">
        <DialogHeader className="border-b border-slate-100 bg-[#fbfaf6] px-6 py-5 pr-12">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]">
              <ReceiptText className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-xl text-slate-950">Régler la facture n°{facture.numero}</DialogTitle>
              <DialogDescription className="mt-1">
                Montant à régler : {(facture.montant / 100).toFixed(2)} €
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 px-6 py-5">
          {facture.montant >= 50 && (
            <section className="rounded-xl border border-slate-200 p-4">
              <div className="flex gap-3">
                <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-[#286351]" />
                <div>
                  <h3 className="font-semibold text-slate-900">Carte bancaire</h3>
                  <p className="mt-1 text-sm leading-5 text-slate-600">
                    Paiement immédiat via le portail sécurisé.
                  </p>
                  <Button
                    className="mt-4 w-full bg-[#173f35] hover:bg-[#23594a] sm:w-auto"
                    onClick={payerParCarte}
                    loading={loading}
                  >
                    {loading ? "Redirection..." : "Payer par carte bancaire"}
                  </Button>
                </div>
              </div>
            </section>
          )}

          <section className="rounded-xl border border-slate-200 p-4">
            <div className="flex gap-3">
              <Landmark className="mt-0.5 h-5 w-5 shrink-0 text-[#286351]" />
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-900">Virement bancaire</h3>
                <p className="mt-1 text-sm leading-5 text-slate-600">
                  Envoyez {(facture.montant / 100).toFixed(2)} € avec la référence {facture.numero}.
                </p>
                <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-700">
                  <p className="break-all">
                    <span className="font-semibold">IBAN :</span>{" "}
                    {facture.sna ? "FR05 3000 2004 3800 0000 7188 H31" : "FR38 3000 2004 3800 0000 7191 U50"}
                  </p>
                  <p>
                    <span className="font-semibold">BIC :</span> CRLYFRPP
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 p-4">
            <div className="flex gap-3">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-[#286351]" />
              <div>
                <h3 className="font-semibold text-slate-900">Chèque</h3>
                <p className="mt-1 text-sm leading-5 text-slate-600">
                  Envoyez un chèque de {(facture.montant / 100).toFixed(2)} € à l'ordre{" "}
                  {facture.sna ? "du Syndicat National d'Apiculture" : "de l'Abeille de France"}, avec la référence{" "}
                  {facture.numero}.
                </p>
                <address className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm not-italic leading-6 text-slate-700">
                  <strong>{facture.sna ? "Syndicat National d'Apiculture" : "L'Abeille de France"}</strong>
                  <br />5 rue des artisans
                  <br />37300 Joué-lès-Tours
                  <br />France
                </address>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter className="border-t border-slate-100 bg-slate-50/70 px-6 py-4">
          <DialogClose asChild>
            <Button variant="outline">Fermer</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CommentPayerFactureDialog;
